/**
 * Path helpers for the nested multi-tenant schema.
 *
 *   organizations/{orgId}/
 *     ├── members/{userId}
 *     ├── individuals/{individualId}
 *     ├── caseloads/{caseloadId}
 *     ├── sessions/{sessionId}
 *     ├── notes/{noteId}
 *     ├── pendingNotes/{noteId}
 *     ├── files/{fileId}
 *     ├── appointments/{apptId}
 *     ├── referrals/{referralId}
 *     ├── assessments/{assessmentId}
 *     ├── claims/{claimId}
 *     ├── safetyAlerts/{alertId}
 *     ├── consents/{consentId}
 *     ├── auditLog/{logId}
 *     └── billingConfig/{configId}
 *
 * Top-level (cross-org) collections that stay flat:
 *   users/{userId}        — auth-tied profile, holds primaryOrgId for lookup
 *   superAdmin/{userId}   — platform admins
 *   syncQueue/{queueId}   — transient offline write queue, scoped per user
 */

import { collection, doc } from "firebase/firestore";
import { db, auth } from "./firebase";

const ORG_KEY = "fieldnotes_user_org";
const DEFAULT_ORG = "default-org";

/**
 * Resolve the active org for the current user.
 *
 * Order of precedence:
 *   1. Explicit argument
 *   2. Cached in localStorage (set by auth flow)
 *   3. Fallback "default-org"
 */
export function currentOrgId(explicit) {
  if (explicit) return explicit;
  try {
    return localStorage.getItem(ORG_KEY) || DEFAULT_ORG;
  } catch {
    return DEFAULT_ORG;
  }
}

export function cacheOrgId(orgId) {
  if (!orgId) return;
  try {
    localStorage.setItem(ORG_KEY, orgId);
  } catch {
    /* localStorage unavailable */
  }
}

/**
 * Return a collection reference under organizations/{orgId}/{name}.
 *   orgCollection("notes")            → organizations/<currentOrg>/notes
 *   orgCollection("notes", "abc123")  → organizations/abc123/notes
 */
export function orgCollection(name, orgIdOverride) {
  const orgId = currentOrgId(orgIdOverride);
  return collection(db, "organizations", orgId, name);
}

/**
 * Return a doc reference under organizations/{orgId}/{name}/{docId}.
 */
export function orgDoc(name, docId, orgIdOverride) {
  const orgId = currentOrgId(orgIdOverride);
  return doc(db, "organizations", orgId, name, docId);
}

/**
 * Return the string path for an org-scoped collection.
 * Useful for Cloud Function trigger paths and Storage prefixes.
 */
export function orgCollectionPath(name, orgIdOverride) {
  const orgId = currentOrgId(orgIdOverride);
  return `organizations/${orgId}/${name}`;
}

/**
 * Return a Firebase Storage path scoped to the active org.
 *   orgStoragePath("audio", userId, sessionId, "recording.webm")
 *   → organizations/<currentOrg>/audio/<userId>/<sessionId>/recording.webm
 */
export function orgStoragePath(...segments) {
  const orgId = currentOrgId();
  return ["organizations", orgId, ...segments.filter(Boolean)].join("/");
}

/**
 * Convenience for the top-level users collection (unchanged by migration).
 */
export function userDoc(uid) {
  return doc(db, "users", uid || auth.currentUser?.uid);
}
